import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Web equivalent of iOS's ActingAthleteContext + NetworkManager thread.
 *
 * When a guardian is acting on a linked athlete, this context holds the
 * athlete id / name / permission and registers an axios request
 * interceptor that auto-appends `?as_athlete_id=N` to every /api/*
 * call (except /api/guardians/* which operate on the caller's own
 * identity). Backend's `get_acting_athlete` dependency reads that
 * query param and authorizes against athlete_guardians.
 *
 * State is persisted to localStorage so a tab reload preserves the
 * acting context — same cold-launch behavior as the iOS app's
 * checkSession path.
 *
 * Cleared on logout via the cleanup helper below (called from
 * AuthContext.logout()).
 */

const STORAGE_ID = 'acting_athlete_id';
const STORAGE_NAME = 'acting_athlete_name';
const STORAGE_PERM = 'acting_athlete_permission';

const ActingAthleteContext = createContext({
  actingAthleteId: null,
  actingAthleteName: null,
  actingPermission: null,
  enterAthlete: () => {},
  exitAthlete: () => {},
});

export function ActingAthleteProvider({ children }) {
  const [actingAthleteId, setActingAthleteId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_ID);
    return stored ? parseInt(stored, 10) : null;
  });
  const [actingAthleteName, setActingAthleteName] = useState(
    () => localStorage.getItem(STORAGE_NAME) || null
  );
  const [actingPermission, setActingPermission] = useState(
    () => localStorage.getItem(STORAGE_PERM) || null
  );

  // Re-install the axios interceptor whenever the acting target changes.
  // Only mounted when an athlete is actively selected; the eject in the
  // cleanup keeps un-acting requests from accidentally carrying the
  // last-acted-on athlete id.
  useEffect(() => {
    if (!actingAthleteId) return undefined;
    const interceptorId = axios.interceptors.request.use((config) => {
      const url = config.url || '';
      // Whitelist /api/ paths excluding /api/guardians/. Athlete
      // endpoints, /users/me/subscription, /videos, /game-stats,
      // /player-cards all expect as_athlete_id when guardian acts.
      if (
        url.includes('/api/') &&
        !url.includes('/api/guardians/')
      ) {
        config.params = config.params || {};
        if (!('as_athlete_id' in config.params)) {
          config.params.as_athlete_id = actingAthleteId;
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptorId);
  }, [actingAthleteId]);

  function enterAthlete(managed) {
    const id = managed.athlete_user_id;
    const name = [managed.athlete_first_name, managed.athlete_last_name]
      .filter(Boolean)
      .join(' ') || managed.athlete_email || `Athlete ${id}`;
    setActingAthleteId(id);
    setActingAthleteName(name);
    setActingPermission(managed.permission || 'edit');
    localStorage.setItem(STORAGE_ID, String(id));
    localStorage.setItem(STORAGE_NAME, name);
    localStorage.setItem(STORAGE_PERM, managed.permission || 'edit');
  }

  function exitAthlete() {
    setActingAthleteId(null);
    setActingAthleteName(null);
    setActingPermission(null);
    clearActingAthleteStorage();
  }

  return (
    <ActingAthleteContext.Provider
      value={{
        actingAthleteId,
        actingAthleteName,
        actingPermission,
        enterAthlete,
        exitAthlete,
      }}
    >
      {children}
    </ActingAthleteContext.Provider>
  );
}

export const useActingAthlete = () => useContext(ActingAthleteContext);

/**
 * Standalone helper for non-React contexts (AuthContext.logout) so they
 * can wipe acting state without holding a Context handle.
 */
export function clearActingAthleteStorage() {
  localStorage.removeItem(STORAGE_ID);
  localStorage.removeItem(STORAGE_NAME);
  localStorage.removeItem(STORAGE_PERM);
}
